import React, { useCallback, useEffect, useState } from 'react';
import { technicalAPI } from '../../services/api';
import { LoadingBlock, EmptyState, SeverityBadge, SEVERITY_COLOR } from '../../components/technical/TechnicalUI';
import { AlertTriangle, Eye, EyeOff, Zap, PowerOff, PauseCircle, HelpCircle } from 'lucide-react';
import useDarkReady from '../../theme/useDarkReady';

const STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'all', label: 'All' },
  { key: 'resolved', label: 'Resolved' },
];

// Why an ONU dropped, as the technician reads it. Mirrors classifyOnuOffline in
// backend/src/services/alarmService.js — a power cut at the customer's house is not the same
// event as our fiber losing signal, and only the second one is anyone's job here.
const CAUSE_META = {
  signal_loss: { label: 'Signal loss', icon: Zap, color: '#991b1b' },
  power_off: { label: 'Power off', icon: PowerOff, color: '#6b7280' },
  deactivated: { label: 'Deactivated', icon: PauseCircle, color: '#6b7280' },
  unknown: { label: 'Reason unknown', icon: HelpCircle, color: '#6b7280' },
};

function AlarmRow({ a }) {
  const c = SEVERITY_COLOR[a.severity];
  const cause = a.meta?.cause ? CAUSE_META[a.meta.cause] : null;
  const CauseIcon = cause?.icon;
  return (
    <div style={{ border: `1px solid ${c.bg}`, borderLeft: `4px solid ${c.color}`, borderRadius: 10, padding: '12px 14px', background: 'var(--cb-ffffff)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <SeverityBadge severity={a.severity} />
          {cause && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4, fontSize: '.7rem', fontWeight: 600,
              padding: '2px 8px', borderRadius: 20, background: `${cause.color}14`, color: cause.color,
            }}>
              <CauseIcon size={11} />{cause.label}
            </span>
          )}
        </div>
        <span style={{ fontSize: '.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>
          {new Date(a.raisedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{a.message}</div>
      {a.source && <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{a.source}</div>}
      {a.status === 'resolved' && (
        <div style={{ fontSize: '.75rem', color: 'var(--c-0ca30c)', marginTop: 4 }}>
          Resolved {a.resolvedAt ? new Date(a.resolvedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
        </div>
      )}
    </div>
  );
}

export default function TechnicalAlarmsPage() {
  useDarkReady();
  const [alarms, setAlarms] = useState(null);
  const [minor, setMinor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('active');
  // Minor alarms are almost entirely customers switching their own power off. They are kept
  // and still fully queryable — they just don't lead, because a list where 32 of 35 rows are
  // routine power cuts is a list nobody reads. One tap brings them back.
  const [showMinor, setShowMinor] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      technicalAPI.getAlarms({ status, severity: 'critical,major' }),
      technicalAPI.getAlarms({ status, severity: 'minor' }),
    ])
      .then(([maj, min]) => { setAlarms(maj.data.data); setMinor(min.data.data); })
      .catch(() => { setAlarms([]); setMinor([]); })
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const minorCount = minor?.length ?? 0;

  return (
    <div>
      <div className="page-header">
        <h2>Alarms</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
          Faults that need a technician. Customer power cuts are kept below.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatus(f.key)}
            style={{
              font: 'inherit', fontSize: '.85rem', minHeight: 44, padding: '0 16px', borderRadius: 22,
              cursor: 'pointer', border: `1.5px solid ${status === f.key ? 'var(--primary)' : 'var(--cb-e5e7eb)'}`,
              background: status === f.key ? 'var(--primary)' : 'var(--cb-ffffff)',
              color: status === f.key ? '#fff' : '#374151', fontWeight: 600,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <LoadingBlock /> : (
        <>
          {(!alarms || alarms.length === 0) ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
              border: '1px solid var(--cb-bbf7d0)', background: 'var(--cb-f0fdf4)', borderRadius: 12, padding: '16px 18px',
            }}>
              <AlertTriangle size={18} color="#0ca30c" />
              <span style={{ fontSize: '.9rem', fontWeight: 600, color: 'var(--c-166534)' }}>
                No major or critical alarms.
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {alarms.map(a => <AlarmRow key={a._id} a={a} />)}
            </div>
          )}

          {minorCount > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowMinor(v => !v)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  minHeight: 48, borderRadius: 12, border: '1px solid var(--cb-e5e7eb)', background: 'var(--cb-ffffff)',
                  font: 'inherit', fontSize: '.85rem', fontWeight: 600, color: '#6b7280', cursor: 'pointer',
                }}
              >
                {showMinor ? <EyeOff size={16} /> : <Eye size={16} />}
                {showMinor ? 'Hide' : 'Show'} {minorCount} minor {minorCount === 1 ? 'alarm' : 'alarms'}
                <span style={{ fontWeight: 400 }}>(power cuts, deactivations)</span>
              </button>

              {showMinor && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                  {minor.map(a => <AlarmRow key={a._id} a={a} />)}
                </div>
              )}
            </>
          )}

          {(!alarms || alarms.length === 0) && minorCount === 0 && (
            <EmptyState icon={AlertTriangle} text="No alarms — all clear." />
          )}
        </>
      )}
    </div>
  );
}
